import { Vector3 } from 'three/webgpu';
import type { HollowDef } from '../universe/HollowDef';
import { perpendicularBasis, type SpiralOptions, type TunnelMouth, wallCrossing } from '../universe/Tunnel';

/**
 * Where a link's mouths are: the parameters of the spiral each cave used to be. They
 * are kept so every mouth, and the terrain shaped around it, stays exactly where it was.
 * `phaseB` is where that spiral ended (see legacyPhaseB); with it, mouth B no longer
 * depends on how far apart the worlds are.
 */
export interface MouthPlan extends SpiralOptions {
  phaseB: number;
}

/** The angle at which a spiral with these options, across `gap` metres of rock, ended. */
export function legacyPhaseB(o: SpiralOptions, gap: number, heightA: number, heightB: number): number {
  const psi = (o.pitchDeg * Math.PI) / 180;
  return o.phase + (gap + heightA + heightB - 2 * o.depth) / (o.helixRadius * Math.tan(psi));
}

export interface EntranceOptions {
  /** Radius of the entrance's own helix (m). */
  helixRadius: number;
  /** How far below the hollow's sea level the gate lies (m). */
  gateDepth: number;
  /** Length of the level stretch that ends at the gate (m). */
  gateLength: number;
  /** Fewest helix turns: enough that nothing in the cave can be seen from the mouth. */
  minTurns: number;
}

/** Defaults by world size: a tighter helix in small worlds, the gate a few metres below sea level. */
export function entranceOptions(worldRadius: number, tubeRadius: number): EntranceOptions {
  const big = worldRadius >= 2000;
  return {
    helixRadius: big ? 12 : 8,
    gateDepth: tubeRadius + 4,
    gateLength: big ? 6 : 5,
    minTurns: 0.75,
  };
}

/**
 * The fixed way down from one mouth to the cave: mouth → straight entry → helix →
 * a quarter turn levelling out → a level stretch → the gate. Walkable against the
 * world's own gravity, it turns at least a full circle, so the mouth can't be seen from
 * the gate or anything past it: a safe place to load the cave (going down) or the world
 * (coming up).
 */
export interface Entrance {
  /** Centre-line from just above the mouth down to the gate (universe, f64). */
  path: Vector3[];
  radius: number;
  /** Exactly the old spiral's mouth. */
  mouth: TunnelMouth;
  /** Helix turns (more than the minimum when the mouth sits high above sea level). */
  turns: number;
  /** Indices in `path`: the helix's first point, where the levelling starts, where the gate stretch starts. */
  helixStart: number;
  levelStart: number;
  gateStart: number;
  /** The gate (the last point), the level direction on into the cave, and "down" there. */
  gate: Vector3;
  gateDir: Vector3;
  down: Vector3;
}

const LEVEL_TURN = Math.PI / 2;
const STEP = 0.5; // metres between helix points
/**
 * How far (m) the walkway's floor clears the ground where the straight entry starts: aimed
 * at over the plane across the link's axis, and the least over the real (curved) plateau.
 */
const ENTRY_CLEAR = 1.5;
const ENTRY_CLEAR_MIN = 1;

const _e = new Vector3();

/**
 * Length of the straight entry, back from `start` along −dir: at least `least` (the length
 * that clears the ground when it's taken as the plane across the link's axis), and on until
 * the walkway's floor clears the plateau, a sphere of radius `plateauR` round `center`, by
 * ENTRY_CLEAR_MIN. In big worlds the plane is within centimetres of the sphere. In a small
 * world the ground curves up toward the entry, which then meets it at a shallower angle than
 * the spiral's pitch and cuts a longer hole: stopped at `least`, the entry left the back of
 * that hole open over bare rock (a 20 km world's spiral at a mouth in a 200 m world: 13 m of
 * it). Stops at the most it can clear, past which the line runs back down toward the ground.
 */
function entryLength(
  start: Vector3,
  dir: Vector3,
  center: Vector3,
  plateauR: number,
  r: number,
  least: number,
) {
  let best = least;
  let bestFloor = Number.NEGATIVE_INFINITY;
  for (let len = least; len <= least + 80; len += 0.25) {
    _e.copy(start).addScaledVector(dir, -len).sub(center);
    const d = _e.length();
    // The entry's climb out of the ground here; the floor lies r / cos of it below the axis.
    const climb = _e.dot(dir) / d;
    const floor = plateauR - d - r / Math.sqrt(Math.max(1e-6, 1 - climb * climb));
    if (floor >= ENTRY_CLEAR_MIN) return len;
    if (floor <= bestFloor) break;
    bestFloor = floor;
    best = len;
  }
  return best;
}

/**
 * Both entrances of a link. `pa`, `pb` are the two hollows with their plateau radii
 * (R − height), as the old spiral took them; `radii` are the real (sea-level) radii.
 */
export function buildEntrances(
  pa: HollowDef,
  pb: HollowDef,
  radii: [number, number],
  plan: MouthPlan,
  opts: [EntranceOptions, EntranceOptions],
): [Entrance, Entrance] {
  const axis = pb.center.clone().sub(pa.center).normalize();
  const n = new Vector3();
  const bb = new Vector3();
  perpendicularBasis(axis, n, bb);
  const psi = (plan.pitchDeg * Math.PI) / 180;
  const r = plan.tubeRadius;

  const build = (end: 0 | 1): Entrance => {
    const o = opts[end];
    const plateau = end === 0 ? pa : pb;
    const center = plateau.center;
    const sign = end === 0 ? 1 : -1; // along the axis, into the rock
    const phase = end === 0 ? plan.phase : plan.phaseB;
    // The old spiral's helix point at this end, and its direction down into the rock.
    const base = center.clone().addScaledVector(axis, sign * plateau.radius);
    const radial = n.clone().multiplyScalar(Math.cos(phase)).addScaledVector(bb, Math.sin(phase));
    const start = base
      .clone()
      .addScaledVector(axis, sign * plan.depth)
      .addScaledVector(radial, plan.helixRadius);
    const tangential = (theta: number, out: Vector3) =>
      out
        .copy(n)
        .multiplyScalar(-Math.sin(theta) * sign)
        .addScaledVector(bb, Math.cos(theta) * sign);
    const dir = tangential(phase, new Vector3())
      .multiplyScalar(Math.cos(psi))
      .addScaledVector(axis, sign * Math.sin(psi));

    // The mouth: where the straight entry's axis crosses the plateau (as before).
    const mouth: TunnelMouth = { hollow: plateau, point: start.clone(), dir: dir.clone(), radius: r };
    mouth.point.addScaledVector(dir, wallCrossing(mouth, new Vector3(), plateau.radius));

    // Straight entry, from just above the ground (so the walkway's floor clears it) to `start`.
    const path: Vector3[] = [];
    const entryLen = entryLength(
      start,
      dir,
      center,
      plateau.radius,
      r,
      (plan.depth + r / Math.cos(psi) + ENTRY_CLEAR) / Math.sin(psi),
    );
    const count = Math.ceil(entryLen / 0.25);
    for (let i = 0; i < count; i++) {
      path.push(start.clone().addScaledVector(dir, -entryLen + (i / count) * entryLen));
    }
    const helixStart = path.length;

    // The helix, around a centre R' in from `start`, then a quarter turn easing to level.
    const R = o.helixRadius;
    const hub = start.clone().addScaledVector(radial, -R);
    const pointAt = (phi: number, drop: number, out: Vector3) => {
      const theta = phase + sign * phi;
      return out
        .copy(hub)
        .addScaledVector(n, R * Math.cos(theta))
        .addScaledVector(bb, R * Math.sin(theta))
        .addScaledVector(axis, sign * drop);
    };
    const dPhi = STEP / R;
    const dropPerRad = R * Math.tan(psi);
    const trace = (turns: number, into: Vector3[] | null) => {
      const helixEnd = turns * Math.PI * 2;
      let drop = 0;
      let phi = 0;
      let levelAt = -1;
      const p = new Vector3();
      while (phi < helixEnd + LEVEL_TURN - 1e-9) {
        const next = Math.min(phi + dPhi, helixEnd + LEVEL_TURN);
        // Pitch: full on the helix, easing to zero over the levelling quarter turn.
        const mid = (phi + next) / 2;
        const t = Math.min(Math.max((mid - helixEnd) / LEVEL_TURN, 0), 1);
        drop += (next - phi) * dropPerRad * (1 - t * t * (3 - 2 * t));
        phi = next;
        if (into) {
          if (levelAt < 0 && phi > helixEnd) levelAt = into.length;
          into.push(pointAt(phi, drop, new Vector3()));
        }
      }
      return { phi, drop, levelAt, end: pointAt(phi, drop, p) };
    };
    // Enough turns that the gate is gateDepth below sea level.
    const worldR = radii[end];
    let turns = o.minTurns;
    for (; turns < 8; turns += 0.05) {
      const { phi, end: p } = trace(turns, null);
      const gate = p.clone().addScaledVector(tangential(phase + sign * phi, new Vector3()), o.gateLength);
      if (gate.distanceTo(center) >= worldR + o.gateDepth) break;
    }
    path.push(start.clone());
    const helix: Vector3[] = [];
    const { phi, levelAt } = trace(turns, helix);
    const levelStart = helixStart + 1 + levelAt;
    path.push(...helix);

    // The level stretch to the gate.
    const gateDir = tangential(phase + sign * phi, new Vector3()).normalize();
    const gateStart = path.length;
    const last = path[path.length - 1].clone();
    const steps = Math.ceil(o.gateLength / STEP);
    for (let i = 1; i <= steps; i++)
      path.push(last.clone().addScaledVector(gateDir, (i / steps) * o.gateLength));
    const gate = path[path.length - 1].clone();
    return {
      path,
      radius: r,
      mouth,
      turns,
      helixStart,
      levelStart,
      gateStart,
      gate,
      gateDir,
      down: gate.clone().sub(center).normalize(),
    };
  };
  return [build(0), build(1)];
}

/**
 * True if a straight line from p to q stays inside the tube along `path` (radius r): one
 * end can then see the other. Samples every half metre.
 */
export function seesThrough(path: Vector3[], r: number, p: Vector3, q: Vector3): boolean {
  const len = p.distanceTo(q);
  const steps = Math.max(2, Math.ceil(len / 0.5));
  const s = new Vector3();
  const seg = new Vector3();
  const w = new Vector3();
  for (let i = 0; i <= steps; i++) {
    s.lerpVectors(p, q, i / steps);
    let best = Number.POSITIVE_INFINITY;
    for (let k = 0; k + 1 < path.length; k++) {
      seg.subVectors(path[k + 1], path[k]);
      w.subVectors(s, path[k]);
      const t = Math.min(Math.max(w.dot(seg) / Math.max(seg.lengthSq(), 1e-12), 0), 1);
      best = Math.min(best, w.addScaledVector(seg, -t).lengthSq());
    }
    if (best > r * r) return false;
  }
  return true;
}
